/**
 * Dokumente (/dokumente, DMS-light): vier Tabs —
 * Dokumente (Grid mit Typ-Badge, Version, gültig ab, Datei-Upload/-Download, Link),
 * Arbeitsanweisungen (Katalog K), Stellenbeschreibungen (Katalog, Backend-Gate
 * personal_schreiben) und Zuordnung Arbeitsanweisung ↔ Arbeitsplatz (N „ap2aa").
 * Schreiben: dokumente_schreiben (alle Leitungsrollen + admin); löschen: loeschen.
 */

import { useMemo, useRef, useState } from "react"
import { keepPreviousData, useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { Download, Paperclip, Pencil, Plus, Trash2 } from "lucide-react"
import { toast } from "sonner"
import { EntityFormSheet, type FieldDef, type FormValues } from "@/components/admin/EntityFormSheet"
import { MasterDataSection, type Column } from "@/components/admin/MasterDataSection"
import { useMasterData } from "@/components/admin/useMasterData"
import { LoadingSpinner } from "@/components/shared"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Card, CardContent } from "@/components/ui/card"
import { DataGrid, type ColumnDef } from "@/components/ui/data-grid"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Select } from "@/components/ui/select"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs"
import { fmtDate } from "@/lib/format"
import { useCan } from "@/lib/permissions"
import { api, apiError } from "@/services/api"
import type {
  DocumentItem,
  Dokumententyp,
  JobDescription,
  JobDescriptionCreate,
  WorkInstruction,
  WorkInstructionCreate,
  WorkplaceInstruction,
} from "@/types"

const DOCUMENT_TYPE: Record<Dokumententyp, { label: string; badge: string }> = {
  ARBEITSANWEISUNG: {
    label: "Arbeitsanweisung",
    badge: "border-blue-300 bg-blue-100 text-blue-800",
  },
  STELLENBESCHREIBUNG: {
    label: "Stellenbeschreibung",
    badge: "border-purple-300 bg-purple-100 text-purple-800",
  },
}

const DOCUMENT_TYPE_VALUES = Object.keys(DOCUMENT_TYPE) as Dokumententyp[]

// ---------------------------------------------------------------------------
// Tab 1: Dokumente (J)
// ---------------------------------------------------------------------------

function DocumentsTab({ canWrite, canDelete }: { canWrite: boolean; canDelete: boolean }) {
  const queryClient = useQueryClient()
  const [q, setQ] = useState("")
  const [typeFilter, setTypeFilter] = useState<Dokumententyp | "">("")
  const [dialog, setDialog] = useState<{ open: boolean; row: DocumentItem | null }>({
    open: false,
    row: null,
  })

  // Datei-Upload: verstecktes File-Input, Ziel-ID im Ref (Muster SickLeavesTab)
  const fileInputRef = useRef<HTMLInputElement>(null)
  const uploadTargetRef = useRef<string | null>(null)

  const workplaces = useQuery({
    queryKey: ["workplaces"],
    queryFn: () => api.organization.workplaces.list({ size: 100 }),
  })
  const workplaceName = (id: number | null) =>
    id != null ? (workplaces.data?.data.find((w) => w.id === String(id))?.name ?? "—") : "—"

  const params = {
    size: 100,
    q: q || undefined,
    documentType: typeFilter || undefined,
  }
  const documents = useQuery({
    queryKey: ["documents", params],
    queryFn: () => api.documents.list(params),
    placeholderData: keepPreviousData,
  })

  function invalidate() {
    queryClient.invalidateQueries({ queryKey: ["documents"] })
  }

  const save = useMutation({
    mutationFn: ({ id, values }: { id: string | null; values: FormValues }) => {
      const body = {
        title: String(values.title).trim(),
        documentType: values.documentType as Dokumententyp,
        documentCode: String(values.documentCode).trim() || null,
        description: String(values.description).trim() || null,
        version: String(values.version).trim() || null,
        validFrom: String(values.validFrom) || null,
        link: String(values.link).trim() || null,
        workplaceId: values.workplaceId ? Number(values.workplaceId) : null,
      }
      return id ? api.documents.update(id, body) : api.documents.create(body)
    },
    onSuccess: (_data, vars) => {
      invalidate()
      toast.success(vars.id ? "Dokument aktualisiert" : "Dokument angelegt")
      setDialog({ open: false, row: null })
    },
    onError: (e) => toast.error(apiError(e)),
  })

  const upload = useMutation({
    mutationFn: ({ id, file }: { id: string; file: File }) => api.documents.uploadFile(id, file),
    onSuccess: () => {
      invalidate()
      toast.success("Datei hochgeladen")
    },
    onError: (e) => toast.error(apiError(e)),
  })

  const del = useMutation({
    mutationFn: (id: string) => api.documents.remove(id),
    onSuccess: () => {
      invalidate()
      toast.success("Dokument gelöscht")
    },
    onError: (e) => toast.error(apiError(e)),
  })

  function startUpload(id: string) {
    uploadTargetRef.current = id
    fileInputRef.current?.click()
  }

  function onFileSelected(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0]
    const id = uploadTargetRef.current
    if (file && id) upload.mutate({ id, file })
    e.target.value = ""
    uploadTargetRef.current = null
  }

  async function download(doc: DocumentItem) {
    try {
      await api.documents.downloadFile(doc.id, `${doc.title}.pdf`)
    } catch (e) {
      toast.error(apiError(e))
    }
  }

  const rows = documents.data?.data ?? []
  const editing = dialog.row

  // Grid-Spalten: header als String, damit die Spalten-Auswahl ihn anzeigen kann
  const columns = useMemo<ColumnDef<DocumentItem>[]>(
    () => [
      {
        id: "title",
        header: "Titel",
        accessorFn: (doc) => doc.title,
        meta: { cellClassName: "font-medium" },
      },
      {
        id: "code",
        header: "Code",
        accessorFn: (doc) => doc.documentCode ?? "",
        cell: ({ row }) => row.original.documentCode ?? "—",
        meta: { cellClassName: "tabular-nums" },
      },
      {
        id: "type",
        header: "Typ",
        accessorFn: (doc) => DOCUMENT_TYPE[doc.documentType].label,
        cell: ({ row }) => (
          <Badge variant="outline" className={DOCUMENT_TYPE[row.original.documentType].badge}>
            {DOCUMENT_TYPE[row.original.documentType].label}
          </Badge>
        ),
      },
      {
        id: "version",
        header: "Version",
        accessorFn: (doc) => doc.version ?? "",
        cell: ({ row }) => row.original.version ?? "—",
        meta: { cellClassName: "tabular-nums" },
      },
      {
        id: "validFrom",
        // ISO-String als Accessor sortiert korrekt
        header: "Gültig ab",
        accessorFn: (doc) => doc.validFrom ?? "",
        cell: ({ row }) =>
          row.original.validFrom ? fmtDate(row.original.validFrom) : "—",
        meta: { cellClassName: "tabular-nums" },
      },
      {
        id: "workplace",
        header: "Arbeitsplatz",
        accessorFn: (doc) => workplaceName(doc.workplaceId),
      },
      {
        id: "link",
        header: "Link",
        accessorFn: (doc) => doc.link ?? "",
        cell: ({ row }) =>
          row.original.link ? (
            <a
              href={row.original.link}
              target="_blank"
              rel="noreferrer"
              className="text-primary underline-offset-2 hover:underline"
            >
              Link
            </a>
          ) : (
            "—"
          ),
      },
      {
        id: "actions",
        header: "Datei / Aktionen",
        enableSorting: false,
        enableHiding: false,
        meta: { headClassName: "w-32 text-right", cellClassName: "text-right" },
        cell: ({ row }) => {
          const doc = row.original
          return (
            <div className="flex justify-end gap-1">
              {doc.filePath && (
                <Button
                  variant="ghost"
                  size="icon"
                  className="size-7"
                  aria-label="Datei herunterladen"
                  title="Datei herunterladen"
                  onClick={() => download(doc)}
                >
                  <Download className="!size-3.5" />
                </Button>
              )}
              {canWrite && (
                <>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="size-7"
                    aria-label="Datei hochladen"
                    title="Datei hochladen"
                    disabled={upload.isPending}
                    onClick={() => startUpload(doc.id)}
                  >
                    <Paperclip className="!size-3.5" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="size-7"
                    aria-label="Bearbeiten"
                    onClick={() => setDialog({ open: true, row: doc })}
                  >
                    <Pencil className="!size-3.5" />
                  </Button>
                </>
              )}
              {canDelete && (
                <Button
                  variant="ghost"
                  size="icon"
                  className="size-7 text-destructive hover:text-destructive"
                  aria-label="Löschen"
                  disabled={del.isPending}
                  onClick={() => {
                    if (window.confirm("Dokument wirklich löschen?")) del.mutate(doc.id)
                  }}
                >
                  <Trash2 className="!size-3.5" />
                </Button>
              )}
            </div>
          )
        },
      },
    ],
    // workplaceName haengt an workplaces.data; download/startUpload sind stabil
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [workplaces.data, canWrite, canDelete, upload.isPending, del.isPending],
  )

  const fields: FieldDef[] = [
    { key: "title", label: "Titel", kind: "text", required: true },
    {
      key: "documentType",
      label: "Dokumententyp",
      kind: "select",
      required: true,
      options: DOCUMENT_TYPE_VALUES.map((t) => ({ value: t, label: DOCUMENT_TYPE[t].label })),
    },
    { key: "documentCode", label: "Dokumenten-Code", kind: "text", placeholder: "z. B. AA-001" },
    { key: "version", label: "Version", kind: "text", placeholder: "z. B. 1.2" },
    { key: "validFrom", label: "Gültig ab", kind: "date" },
    { key: "link", label: "Link", kind: "text", placeholder: "https://…" },
    {
      key: "workplaceId",
      label: "Arbeitsplatz",
      kind: "select",
      emptyOption: "— kein Arbeitsplatz —",
      options: (workplaces.data?.data ?? []).map((w) => ({ value: w.id, label: w.name })),
    },
    { key: "description", label: "Beschreibung", kind: "textarea" },
  ]

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div className="flex flex-wrap items-end gap-3">
          <div className="w-64 space-y-1.5">
            <Label>Suche</Label>
            <Input
              placeholder="Titel oder Code…"
              value={q}
              onChange={(e) => setQ(e.target.value)}
            />
          </div>
          <div className="w-52 space-y-1.5">
            <Label>Typ</Label>
            <Select
              value={typeFilter}
              onChange={(e) => setTypeFilter(e.target.value as Dokumententyp | "")}
            >
              <option value="">Alle Typen</option>
              {DOCUMENT_TYPE_VALUES.map((t) => (
                <option key={t} value={t}>
                  {DOCUMENT_TYPE[t].label}
                </option>
              ))}
            </Select>
          </div>
        </div>
        {canWrite && (
          <Button onClick={() => setDialog({ open: true, row: null })}>
            <Plus /> Neues Dokument
          </Button>
        )}
      </div>

      <Card>
        <CardContent className="py-2">
          {documents.isLoading ? (
            <LoadingSpinner fullPage />
          ) : (
            <DataGrid
              columns={columns}
              data={rows}
              storageKey="documents"
              // Server-Suche oben filtert bereits ueber ALLE Dokumente —
              // der Grid-Schnellfilter (nur geladene Seite) wuerde verwirren
              enableQuickFilter={false}
              emptyMessage="Keine Dokumente vorhanden."
            />
          )}
        </CardContent>
      </Card>

      {/* Storage erlaubt PDF/JPEG/PNG/WebP (Server validiert) */}
      <input
        ref={fileInputRef}
        type="file"
        className="hidden"
        accept="application/pdf,image/jpeg,image/png,image/webp"
        onChange={onFileSelected}
      />

      <EntityFormSheet
        sheetKey="entity-form-documents"
        open={dialog.open}
        onOpenChange={(open) => setDialog((s) => ({ ...s, open }))}
        title={editing ? "Dokument bearbeiten" : "Neues Dokument"}
        fields={fields}
        initialValues={{
          title: editing?.title ?? "",
          documentType: editing?.documentType ?? "ARBEITSANWEISUNG",
          documentCode: editing?.documentCode ?? "",
          version: editing?.version ?? "",
          validFrom: editing?.validFrom ?? "",
          link: editing?.link ?? "",
          workplaceId: editing?.workplaceId != null ? String(editing.workplaceId) : "",
          description: editing?.description ?? "",
        }}
        pending={save.isPending}
        onSubmit={(values) => save.mutate({ id: editing?.id ?? null, values })}
      />
    </div>
  )
}

// ---------------------------------------------------------------------------
// Tab 2: Arbeitsanweisungen (K)
// ---------------------------------------------------------------------------

function WorkInstructionsTab({
  canWrite,
  canDelete,
}: {
  canWrite: boolean
  canDelete: boolean
}) {
  const crud = useMasterData<WorkInstruction, WorkInstructionCreate>(
    "work-instructions",
    api.documents.workInstructions,
    "Arbeitsanweisung"
  )
  const documents = useQuery({
    queryKey: ["documents", { size: 100 }],
    queryFn: () => api.documents.list({ size: 100 }),
  })
  const documentTitle = (id: number | null) =>
    id != null ? (documents.data?.data.find((d) => d.id === String(id))?.title ?? `#${id}`) : "—"

  const columns: Column<WorkInstruction>[] = [
    { header: "Name", cell: (w) => <span className="font-medium">{w.name}</span> },
    { header: "Code", className: "tabular-nums", cell: (w) => w.code ?? "—" },
    { header: "Dokument", cell: (w) => documentTitle(w.documentId) },
  ]

  const fields: FieldDef[] = [
    { key: "name", label: "Name", kind: "text", required: true },
    { key: "code", label: "Code", kind: "text" },
    {
      key: "documentId",
      label: "Dokument",
      kind: "select",
      emptyOption: "— kein Dokument —",
      options: (documents.data?.data ?? []).map((d) => ({ value: d.id, label: d.title })),
    },
  ]

  return (
    <>
      <MasterDataSection
        isLoading={crud.isLoading}
        rows={crud.rows}
        columns={columns}
        storageKey="masterdata-arbeitsanweisungen"
        emptyText="Keine Arbeitsanweisungen vorhanden."
        canWrite={canWrite}
        canDelete={canDelete}
        createLabel="Neue Arbeitsanweisung"
        onCreate={() => crud.setDialog({ open: true, row: null })}
        onEdit={(row) => crud.setDialog({ open: true, row })}
        onDelete={crud.confirmDelete}
        deletePending={crud.del.isPending}
      />
      <EntityFormSheet
        sheetKey="entity-form-documents"
        open={crud.dialog.open}
        onOpenChange={(open) => crud.setDialog((s) => ({ ...s, open }))}
        title={crud.dialog.row ? "Arbeitsanweisung bearbeiten" : "Neue Arbeitsanweisung"}
        fields={fields}
        initialValues={{
          name: crud.dialog.row?.name ?? "",
          code: crud.dialog.row?.code ?? "",
          documentId:
            crud.dialog.row?.documentId != null ? String(crud.dialog.row.documentId) : "",
        }}
        pending={crud.save.isPending}
        onSubmit={(v) =>
          crud.save.mutate({
            id: crud.dialog.row?.id ?? null,
            body: {
              name: String(v.name).trim(),
              code: String(v.code).trim() || null,
              documentId: v.documentId ? Number(v.documentId) : null,
            },
          })
        }
      />
    </>
  )
}

// ---------------------------------------------------------------------------
// Tab 3: Stellenbeschreibungen (Katalog; Backend-Gate personal_schreiben)
// ---------------------------------------------------------------------------

function JobDescriptionsTab({
  canWrite,
  canDelete,
}: {
  canWrite: boolean
  canDelete: boolean
}) {
  const crud = useMasterData<JobDescription, JobDescriptionCreate>(
    "job-descriptions",
    api.organization.jobDescriptions,
    "Stellenbeschreibung"
  )
  const documents = useQuery({
    queryKey: ["documents", { size: 100 }],
    queryFn: () => api.documents.list({ size: 100 }),
  })
  const documentTitle = (id: string | null) =>
    id != null ? (documents.data?.data.find((d) => d.id === id)?.title ?? `#${id}`) : "—"

  const columns: Column<JobDescription>[] = [
    { header: "Name", cell: (j) => <span className="font-medium">{j.name}</span> },
    { header: "Code", className: "tabular-nums", cell: (j) => j.code ?? "—" },
    { header: "Dokument", cell: (j) => documentTitle(j.documentId) },
  ]

  const fields: FieldDef[] = [
    { key: "name", label: "Name", kind: "text", required: true },
    { key: "code", label: "Code", kind: "text" },
    {
      key: "documentId",
      label: "Dokument",
      kind: "select",
      emptyOption: "— kein Dokument —",
      options: (documents.data?.data ?? []).map((d) => ({ value: d.id, label: d.title })),
    },
  ]

  return (
    <>
      <MasterDataSection
        isLoading={crud.isLoading}
        rows={crud.rows}
        columns={columns}
        storageKey="masterdata-stellenbeschreibungen-docs"
        emptyText="Keine Stellenbeschreibungen vorhanden."
        canWrite={canWrite}
        canDelete={canDelete}
        createLabel="Neue Stellenbeschreibung"
        onCreate={() => crud.setDialog({ open: true, row: null })}
        onEdit={(row) => crud.setDialog({ open: true, row })}
        onDelete={crud.confirmDelete}
        deletePending={crud.del.isPending}
      />
      <EntityFormSheet
        sheetKey="entity-form-documents"
        open={crud.dialog.open}
        onOpenChange={(open) => crud.setDialog((s) => ({ ...s, open }))}
        title={crud.dialog.row ? "Stellenbeschreibung bearbeiten" : "Neue Stellenbeschreibung"}
        fields={fields}
        initialValues={{
          name: crud.dialog.row?.name ?? "",
          code: crud.dialog.row?.code ?? "",
          documentId: crud.dialog.row?.documentId ?? "",
        }}
        pending={crud.save.isPending}
        onSubmit={(v) =>
          crud.save.mutate({
            id: crud.dialog.row?.id ?? null,
            body: {
              name: String(v.name).trim(),
              code: String(v.code).trim() || null,
              documentId: v.documentId ? Number(v.documentId) : null,
            },
          })
        }
      />
    </>
  )
}

// ---------------------------------------------------------------------------
// Tab 4: Zuordnung Arbeitsanweisung ↔ Arbeitsplatz (N „ap2aa")
// ---------------------------------------------------------------------------

function MappingTab({ canWrite }: { canWrite: boolean }) {
  const queryClient = useQueryClient()
  const [workplaceFilter, setWorkplaceFilter] = useState("")
  const [addOpen, setAddOpen] = useState(false)

  const workplaces = useQuery({
    queryKey: ["workplaces"],
    queryFn: () => api.organization.workplaces.list({ size: 100 }),
  })
  const instructions = useQuery({
    queryKey: ["work-instructions"],
    queryFn: () => api.documents.workInstructions.list({ size: 100 }),
  })

  const params = {
    size: 100,
    workplace: workplaceFilter ? Number(workplaceFilter) : undefined,
  }
  const links = useQuery({
    queryKey: ["workplace-instructions", params],
    queryFn: () => api.documents.workplaceInstructions.list(params),
    placeholderData: keepPreviousData,
  })

  const add = useMutation({
    mutationFn: (values: FormValues) =>
      api.documents.workplaceInstructions.add({
        workInstructionId: Number(values.workInstructionId),
        workplaceId: Number(values.workplaceId),
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["workplace-instructions"] })
      toast.success("Zuordnung angelegt")
      setAddOpen(false)
    },
    onError: (e) => toast.error(apiError(e)),
  })

  const remove = useMutation({
    mutationFn: (id: string) => api.documents.workplaceInstructions.remove(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["workplace-instructions"] })
      toast.success("Zuordnung entfernt")
    },
    onError: (e) => toast.error(apiError(e)),
  })

  const rows = links.data?.data ?? []

  // Grid-Spalten: header als String, damit die Spalten-Auswahl ihn anzeigen kann
  const columns = useMemo<ColumnDef<WorkplaceInstruction>[]>(
    () => [
      {
        id: "workInstruction",
        header: "Arbeitsanweisung",
        accessorFn: (link) => link.workInstructionName,
        meta: { cellClassName: "font-medium" },
      },
      {
        id: "workplace",
        header: "Arbeitsplatz",
        accessorFn: (link) => link.workplaceName,
      },
      ...(canWrite
        ? ([
            {
              id: "actions",
              header: "Aktionen",
              enableSorting: false,
              enableHiding: false,
              meta: { headClassName: "w-20 text-right", cellClassName: "text-right" },
              cell: ({ row }) => (
                <Button
                  variant="ghost"
                  size="icon"
                  className="size-7 text-destructive hover:text-destructive"
                  aria-label="Entfernen"
                  disabled={remove.isPending}
                  onClick={() => {
                    if (window.confirm("Zuordnung wirklich entfernen?"))
                      remove.mutate(row.original.id)
                  }}
                >
                  <Trash2 className="!size-3.5" />
                </Button>
              ),
            },
          ] satisfies ColumnDef<WorkplaceInstruction>[])
        : []),
    ],
    // remove ist eine stabile Mutation-Referenz
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [canWrite, remove.isPending],
  )

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div className="w-64 space-y-1.5">
          <Label>Arbeitsplatz</Label>
          <Select value={workplaceFilter} onChange={(e) => setWorkplaceFilter(e.target.value)}>
            <option value="">Alle Arbeitsplätze</option>
            {(workplaces.data?.data ?? []).map((w) => (
              <option key={w.id} value={w.id}>
                {w.name}
              </option>
            ))}
          </Select>
        </div>
        {canWrite && (
          <Button onClick={() => setAddOpen(true)}>
            <Plus /> Neue Zuordnung
          </Button>
        )}
      </div>

      <Card>
        <CardContent className="py-2">
          {links.isLoading ? (
            <LoadingSpinner fullPage />
          ) : (
            <DataGrid
              columns={columns}
              data={rows}
              storageKey="documents-zuordnung"
              emptyMessage="Keine Zuordnungen vorhanden."
            />
          )}
        </CardContent>
      </Card>

      <EntityFormSheet
        sheetKey="entity-form-documents"
        open={addOpen}
        onOpenChange={setAddOpen}
        title="Neue Zuordnung Arbeitsanweisung ↔ Arbeitsplatz"
        fields={[
          {
            key: "workInstructionId",
            label: "Arbeitsanweisung",
            kind: "select",
            required: true,
            emptyOption: "— wählen —",
            options: (instructions.data?.data ?? []).map((w) => ({
              value: w.id,
              label: w.name,
            })),
          },
          {
            key: "workplaceId",
            label: "Arbeitsplatz",
            kind: "select",
            required: true,
            emptyOption: "— wählen —",
            options: (workplaces.data?.data ?? []).map((w) => ({ value: w.id, label: w.name })),
          },
        ]}
        initialValues={{ workInstructionId: "", workplaceId: "" }}
        pending={add.isPending}
        onSubmit={(values) => add.mutate(values)}
      />
    </div>
  )
}

// ---------------------------------------------------------------------------
// Seite
// ---------------------------------------------------------------------------

export function DocumentsPage() {
  const canWrite = useCan("dokumente_schreiben")
  const canWritePersonal = useCan("personal_schreiben")
  const canDelete = useCan("loeschen")
  const [tab, setTab] = useState("dokumente")

  return (
    <div className="space-y-4">
      <h1 className="text-2xl font-semibold tracking-tight">Dokumente</h1>
      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="flex-wrap">
          <TabsTrigger value="dokumente">Dokumente</TabsTrigger>
          <TabsTrigger value="arbeitsanweisungen">Arbeitsanweisungen</TabsTrigger>
          <TabsTrigger value="stellenbeschreibungen">Stellenbeschreibungen</TabsTrigger>
          <TabsTrigger value="zuordnung">Zuordnung AA ↔ Arbeitsplatz</TabsTrigger>
        </TabsList>
        <TabsContent value="dokumente">
          <DocumentsTab canWrite={canWrite} canDelete={canDelete} />
        </TabsContent>
        <TabsContent value="arbeitsanweisungen">
          <WorkInstructionsTab canWrite={canWrite} canDelete={canDelete} />
        </TabsContent>
        <TabsContent value="stellenbeschreibungen">
          <JobDescriptionsTab canWrite={canWritePersonal} canDelete={canDelete} />
        </TabsContent>
        <TabsContent value="zuordnung">
          <MappingTab canWrite={canWrite} />
        </TabsContent>
      </Tabs>
    </div>
  )
}
